import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Flex,
  Heading,
  IconButton,
  Loader,
  Text,
} from '@vibe/core';
import { Delete, Edit } from '@vibe/icons';
import { useMondayContext } from '../context/MondayContext';
import { getBoardConfig } from '../api/board-config.api';
import { fetchItemWithColumns, fetchMondayUsers, type MondayUser } from '../api/monday.api';
import {
  getWorklogsByItem,
  createWorklog,
  updateWorklog,
  deleteWorklog,
  type Worklog,
} from '../api/worklog.api';
import WorklogForm from '../components/worklog/WorklogForm';
import { formatDate } from '../utils/dates';
import { formatHoursMinutes } from '../utils/timesheet-capacity';

/**
 * Item View surface entry point.
 *
 * Shows for a single monday.com item:
 * - Estimate summary: Original Estimate, Logged, Remaining
 * - Worklog entries list with CRUD
 */

interface EstimateSummary {
  originalEstimate: number | null;
  totalLogged: number;
  remaining: number | null;
}

const ItemView: React.FC = () => {
  const { context, isReady } = useMondayContext();

  // Allow the page to scroll naturally (override global overflow:hidden)
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const origHtml = { height: html.style.height, overflow: html.style.overflow };
    const origBody = { height: body.style.height, overflow: body.style.overflow };
    const origRoot = root ? { height: root.style.height, overflow: root.style.overflow } : null;

    html.style.height = 'auto';
    html.style.overflow = 'auto';
    body.style.height = 'auto';
    body.style.overflow = 'auto';
    if (root) {
      root.style.height = 'auto';
      root.style.overflow = 'auto';
    }

    return () => {
      html.style.height = origHtml.height;
      html.style.overflow = origHtml.overflow;
      body.style.height = origBody.height;
      body.style.overflow = origBody.overflow;
      if (root && origRoot) {
        root.style.height = origRoot.height;
        root.style.overflow = origRoot.overflow;
      }
    };
  }, []);

  const [worklogs, setWorklogs] = useState<Worklog[]>([]);
  const [userMap, setUserMap] = useState<Map<string, MondayUser>>(new Map());
  const [estimate, setEstimate] = useState<EstimateSummary>({
    originalEstimate: null,
    totalLogged: 0,
    remaining: null,
  });
  const [itemName, setItemName] = useState('');
  const [boardId, setBoardId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mondayItemId = context.itemId;
  const userId = context.userId;

  // Fetch item data, board config, and worklogs
  const fetchData = useCallback(async () => {
    if (!mondayItemId) return;
    setIsLoading(true);
    setError(null);

    try {
      const [worklogData, itemData, users] = await Promise.all([
        getWorklogsByItem(String(mondayItemId)),
        fetchItemWithColumns(String(mondayItemId)),
        fetchMondayUsers(),
      ]);

      worklogData.sort((a, b) => b.date.localeCompare(a.date));
      setWorklogs(worklogData);

      const uMap = new Map<string, MondayUser>();
      for (const u of users) {
        uMap.set(String(u.id), u);
      }
      setUserMap(uMap);
      setItemName(itemData?.name ?? '');

      const itemBoardId = itemData?.boardId ?? '';
      setBoardId(itemBoardId);

      const totalLogged = worklogData.reduce((sum, w) => sum + w.hours, 0);

      let originalEstimate: number | null = null;
      if (itemBoardId && itemData) {
        try {
          const config = await getBoardConfig(Number(itemBoardId));
          const estimateColumnId = config.fieldMapping?.scheduledHoursColumnId;
          if (estimateColumnId) {
            const col = itemData.columnValues.find(
              (c) => c.id === estimateColumnId,
            );
            if (col?.text) {
              const parsed = parseFloat(col.text);
              if (!isNaN(parsed)) {
                originalEstimate = parsed;
              }
            }
          }
        } catch {
          // Board config might not exist yet
        }
      }

      setEstimate({
        originalEstimate,
        totalLogged,
        remaining:
          originalEstimate !== null ? originalEstimate - totalLogged : null,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load item data',
      );
    } finally {
      setIsLoading(false);
    }
  }, [mondayItemId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const recalcEstimate = useCallback(
    (updatedWorklogs: Worklog[]) => {
      const totalLogged = updatedWorklogs.reduce((sum, w) => sum + w.hours, 0);
      setEstimate((prev) => ({
        ...prev,
        totalLogged,
        remaining:
          prev.originalEstimate !== null
            ? prev.originalEstimate - totalLogged
            : null,
      }));
    },
    [],
  );

  const handleCreate = useCallback(
    async (data: { date: string; hours: number; description: string; worklogCategoryId?: string }) => {
      if (!mondayItemId) return;
      setIsSaving(true);
      setError(null);
      try {
        const created = await createWorklog({
          mondayItemId: String(mondayItemId),
          mondayBoardId: boardId,
          mondayUserId: String(userId ?? 0),
          date: data.date,
          hours: data.hours,
          worklogCategoryId: data.worklogCategoryId,
          description: data.description || null,
        });
        const updated = [created, ...worklogs].sort((a, b) => b.date.localeCompare(a.date));
        setWorklogs(updated);
        recalcEstimate(updated);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to log time');
      } finally {
        setIsSaving(false);
      }
    },
    [mondayItemId, boardId, userId, worklogs, recalcEstimate],
  );

  const handleUpdate = useCallback(
    async (
      id: string,
      data: { date: string; hours: number; description: string; worklogCategoryId?: string },
    ) => {
      setIsSaving(true);
      setError(null);
      try {
        const updatedEntry = await updateWorklog(id, {
          date: data.date,
          hours: data.hours,
          description: data.description,
          worklogCategoryId: data.worklogCategoryId,
        });
        const updated = worklogs.map((w) =>
          w.id === id ? updatedEntry : w,
        );
        setWorklogs(updated);
        recalcEstimate(updated);
        setEditingId(null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to update worklog',
        );
      } finally {
        setIsSaving(false);
      }
    },
    [worklogs, recalcEstimate],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteWorklog(id);
        const updated = worklogs.filter((w) => w.id !== id);
        setWorklogs(updated);
        recalcEstimate(updated);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to delete worklog',
        );
      }
    },
    [worklogs, recalcEstimate],
  );

  if (!isReady) {
    return (
      <div className="rp-view-loading">
        <Loader size={Loader.sizes.MEDIUM} />
      </div>
    );
  }

  if (!mondayItemId) {
    return (
      <div className="rp-view-error">
        <Text>No item selected. Open this view from an item.</Text>
      </div>
    );
  }

  return (
    <div className="rp-item-view">
      {/* Header */}
      <Heading type={Heading.types.H3} className="rp-item-view__title">
        {itemName || 'Item View'}
      </Heading>

      {/* Estimate Summary Cards */}
      <div className="rp-item-view__estimates">
        <div className="rp-item-view__estimate-card">
          <Text type={Text.types.TEXT2} className="rp-item-view__estimate-label">
            Original Estimate
          </Text>
          <Text
            type={Text.types.TEXT1}
            weight={Text.weights.BOLD}
            className="rp-item-view__estimate-value"
          >
            {estimate.originalEstimate !== null
              ? formatHoursMinutes(estimate.originalEstimate)
              : '—'}
          </Text>
        </div>

        <div className="rp-item-view__estimate-card">
          <Text type={Text.types.TEXT2} className="rp-item-view__estimate-label">
            Logged
          </Text>
          <Text
            type={Text.types.TEXT1}
            weight={Text.weights.BOLD}
            className="rp-item-view__estimate-value rp-item-view__estimate-value--logged"
          >
            {formatHoursMinutes(estimate.totalLogged)}
          </Text>
        </div>

        <div className="rp-item-view__estimate-card">
          <Text type={Text.types.TEXT2} className="rp-item-view__estimate-label">
            Remaining
          </Text>
          <Text
            type={Text.types.TEXT1}
            weight={Text.weights.BOLD}
            className={`rp-item-view__estimate-value ${
              estimate.remaining !== null && estimate.remaining < 0
                ? 'rp-item-view__estimate-value--over'
                : ''
            }`}
          >
            {estimate.remaining !== null
              ? formatHoursMinutes(Math.max(0, estimate.remaining))
              : '—'}
          </Text>
        </div>
      </div>

      {isLoading && (
        <div className="rp-worklog-panel__loading">
          <Loader size={Loader.sizes.SMALL} />
        </div>
      )}

      {error && (
        <Text
          type={Text.types.TEXT2}
          style={{ color: '#e2445c', margin: '8px 0' }}
        >
          {error}
        </Text>
      )}

      {!isLoading && (
        <>
          <Divider />

          {/* Log Time form */}
          <div className="rp-worklog-panel__form-section">
            <Text
              type={Text.types.TEXT1}
              weight={Text.weights.MEDIUM}
              style={{ marginBottom: '8px' }}
            >
              Log Time
            </Text>
            <WorklogForm onSubmit={handleCreate} isSaving={isSaving} />
          </div>

          <Divider />

          {/* Worklog entries */}
          <div className="rp-worklog-panel__entries">
            <Text
              type={Text.types.TEXT1}
              weight={Text.weights.MEDIUM}
              style={{ marginBottom: '8px' }}
            >
              Entries ({worklogs.length})
            </Text>

            {worklogs.length === 0 && (
              <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                No time logged yet.
              </Text>
            )}

            {worklogs.map((worklog) => (
              <div key={worklog.id} className="rp-worklog-panel__entry">
                {editingId === worklog.id ? (
                  <div className="rp-worklog-panel__entry-edit">
                    <WorklogForm
                      initialValues={{
                        date: worklog.date,
                        hours: worklog.hours,
                        description: worklog.description ?? '',
                        worklogCategoryId: worklog.worklogCategoryId,
                      }}
                      onSubmit={(data) => handleUpdate(worklog.id, data)}
                      isSaving={isSaving}
                    />
                    <Button
                      kind={Button.kinds.TERTIARY}
                      size={Button.sizes.SMALL}
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Flex
                    justify={Flex.justify.SPACE_BETWEEN}
                    align={Flex.align.START}
                  >
                    <div className="rp-worklog-panel__entry-info">
                      <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL}>
                        {(() => {
                          const user = userMap.get(worklog.mondayUserId);
                          return user ? (
                            <Flex align={Flex.align.CENTER} gap={Flex.gaps.XS} className="rp-worklog-entry__user">
                              {user.photo_thumb_small && (
                                <img
                                  src={user.photo_thumb_small}
                                  alt={user.name}
                                  className="rp-worklog-entry__avatar"
                                />
                              )}
                              <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                                {user.name}
                              </Text>
                            </Flex>
                          ) : null;
                        })()}
                        <Text
                          type={Text.types.TEXT1}
                          weight={Text.weights.MEDIUM}
                        >
                          {formatHoursMinutes(worklog.hours)}
                        </Text>
                        <Text
                          type={Text.types.TEXT2}
                          style={{ color: '#676879' }}
                        >
                          {formatDate(worklog.date, 'MMM d, yyyy')}
                        </Text>
                      </Flex>
                      {worklog.description && (
                        <Text
                          type={Text.types.TEXT2}
                          style={{ color: '#676879', marginTop: '2px' }}
                        >
                          {worklog.description}
                        </Text>
                      )}
                      {worklog.createdByUserId && worklog.createdByUserId !== worklog.mondayUserId && (
                        <Text
                          type={Text.types.TEXT2}
                          style={{ color: '#676879', fontSize: '11px', marginTop: '2px' }}
                        >
                          Created by: {userMap.get(worklog.createdByUserId)?.name ?? `User #${worklog.createdByUserId}`}
                        </Text>
                      )}
                    </div>
                    <Flex gap={Flex.gaps.XS}>
                      <IconButton
                        icon={Edit}
                        size={IconButton.sizes.SMALL}
                        onClick={() => setEditingId(worklog.id)}
                        ariaLabel="Edit worklog"
                      />
                      <IconButton
                        icon={Delete}
                        size={IconButton.sizes.SMALL}
                        onClick={() => handleDelete(worklog.id)}
                        ariaLabel="Delete worklog"
                      />
                    </Flex>
                  </Flex>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ItemView;